"use client"
// dummy data
import {
  kpiData,
  projectOverview,
  upcomingDeadlines,
} from "../data/dashboardDummyData";

import React, { useEffect, useState, useContext } from "react"
import { Link, Routes, Route, Navigate, useNavigate } from "react-router-dom"
import {
  X,
  ArrowUp,
  ArrowDown,
  Settings,
  UserPlus,
  Plus,
  LogOut,
  Menu,
  Building2,
  Bell,
  ChevronDown,
  ArrowUpRight,
  Shield,
  Gauge,
  AlertCircle,
} from "lucide-react"
import ThreeSphere from "shared/components/ThreeSphere"
// Ironworkers feature
import IronworkersPage from "../../Ironworkers/pages/IronworkersPage";
import PropTypes from "prop-types"
import { AuthContext } from "../../Auth/context/AuthContext"
import { jwtDecode } from "jwt-decode"
import InviteMemberModal from "../../Invites/components/InviteMemberModal";
// Foremen feature
import ForemenPage from "../../Foremen/pages/ForemenPage";
import ContactsCRMPage from "../../ContactManagement/pages/ContactsCRMPage";
import CostCodesCRM from "../../CostCodes/index";
import ProjectsPage from "../../Projects/pages/ProjectsPage";
import ProjectDetailPage from "../../Projects/pages/ProjectDetailPage";
import SeatsPage from "../../Seats/pages/SeatsPage";
// Project managers feature
import ProjectManagersPage from "../../ProjectManagers/pages/ProjectManagersPage";
import SidebarNav from "../components/SidebarNav";
import ComingSoonPage from "../pages/ComingSoonPage";
// import TimesheetsPage from "../../TimeSheets/TimesheetsPage"; // Org view timesheets coming soon

function OrganizationDashboard() {
  const { auth, logout } = useContext(AuthContext)
  const navigate = useNavigate()

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [subscription, setSubscription] = useState(null)
  const [showModal, setShowModal] = useState(false) // Control the invite modal

  // Open the modal when the "Invite Team" button is clicked
    const handleInviteClick = () => {
      console.log("Invite Button Clicked!");
      setShowModal(true);
    }
  // Optional: callback for when the invite is successfully sent
  const handleInviteSuccess = () => {
    setShowModal(false)
  }

  // Fetch subscription info on mount
  useEffect(() => {
    if (!auth?.token) return
    fetch("/api/myteconstruction/organization/me", {
      headers: { Authorization: `Bearer ${auth.token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.organization?.subscription) {
          setSubscription(data.organization.subscription)
        }
      })
      .catch(console.error)
  }, [auth?.token])

  // Compute remaining trial days
  const trialDaysRemaining =
    subscription?.status === "trial"
      ? Math.ceil((new Date(subscription.trialEnd) - new Date()) / (1000 * 60 * 60 * 24))
      : 0

  // Check if subscription is inactive
  const isInactive = subscription?.status === "inactive"

  // Logout handler
  const handleLogout = () => {
    logout()
    navigate("/myteconstruction/login")
  }

  // If not logged in, show a login prompt
  if (!auth?.token) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-4 flex items-center justify-center">
        <div className="bg-gray-900 p-6 rounded-lg shadow-lg border border-gray-800 max-w-md w-full text-center">
          <Shield className="h-12 w-12 mx-auto mb-4 text-blue-500" />
          <p className="text-lg font-medium">Please log in to view the Organization Dashboard.</p>
          <Link
            to="/myteconstruction/login"
            className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login <ArrowUpRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <aside
          className={`fixed md:static inset-y-0 left-0 w-64 bg-gray-900 transform ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 transition-transform duration-300 ease-in-out z-30 shadow-xl`}
        >
          <div className="p-3 border-b border-gray-800 flex justify-between items-center">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <img src="/icon.png" alt="Myte Steel" className="h-7 w-7" />
              <span>Myte Steel</span>
            </h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="md:hidden text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-800"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <nav className="p-2">
            <SidebarNav />
            {/* Divider + Logout below nav items */}
            <div className="pt-3 mt-3 border-t border-gray-800 px-1">
              <button
                onClick={handleLogout}
                className="flex items-center w-full text-left py-2 px-3 rounded-lg hover:bg-red-900/30 transition-colors duration-200 text-gray-300 hover:text-white group"
              >
                <LogOut className="w-5 h-5 mr-3 text-red-500 group-hover:text-red-400" />
                <span>Logout</span>
              </button>
            </div>
          </nav>
        </aside>

        {/* Overlay for Mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          ></div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-screen">
          {/* Sticky Top Bar */}
          <header className="bg-gray-900 h-14 flex items-center justify-between px-4 border-b border-gray-800 sticky top-0 z-10 shadow-md">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Mobile Menu Toggle moved into header */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="md:hidden p-2 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 flex items-center justify-center shadow-md"
                aria-label="Toggle menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              {/* Updated Invite Team Button */}
              <button
                onClick={handleInviteClick}
                className="px-2 py-1 bg-blue-600 hover:bg-blue-700 rounded-md text-xs font-medium transition-colors duration-200 flex items-center shadow-md sm:px-3 sm:py-1.5 sm:text-sm"
              >
                <UserPlus className="w-3 h-3 mr-1 sm:w-4 sm:h-4 sm:mr-1.5" />
                <span>Invite Team</span>
              </button>

              <button className="px-2 py-1 bg-green-600 hover:bg-green-700 rounded-md text-xs font-medium transition-colors duration-200 flex items-center shadow-md sm:px-3 sm:py-1.5 sm:text-sm">
                <Plus className="w-3 h-3 mr-1 sm:w-4 sm:h-4 sm:mr-1.5" />
                <span>New Project</span>
              </button>
            </div>

            {/* Trial/Subscription Indicators */}
            <div className="flex items-center gap-3">
              {subscription && subscription.status === "trial" && (
                <div className="bg-yellow-600 px-3 py-1 rounded-md text-sm shadow flex items-center gap-1.5">
                  <Gauge className="w-4 h-4" />
                  {trialDaysRemaining > 0 ? `Trial: ${trialDaysRemaining} day(s) left` : "Trial ends soon!"}
                </div>
              )}
              {isInactive && (
                <div className="bg-red-600 px-3 py-1 rounded-md text-sm shadow flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" />
                  Subscription Inactive:
                  <a href="/myteconstruction/upgrade" className="underline ml-1">
                    Upgrade
                  </a>
                </div>
              )}
              <button
                className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors duration-200 relative"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              <button
                className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors duration-200"
                aria-label="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
              <div className="relative group">
                <button
                  className="flex items-center gap-2 p-1 hover:bg-gray-800 rounded-lg transition-colors duration-200"
                  aria-label="Profile"
                >
                  <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-sm font-medium">
                    JD
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg py-1 border border-gray-700 hidden group-hover:block z-20">
                  <a
                    href="/profile"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    Your Profile
                  </a>
                  <a
                    href="/settings"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    Account Settings
                  </a>
                  <a
                    href="/billing"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
                  >
                    Billing
                  </a>
                  <div className="border-t border-gray-700 my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* Render the invite modal if triggered */}
          {showModal && (
              <InviteMemberModal
                onClose={() => setShowModal(false)}
                onSuccess={handleInviteSuccess}
              />
            )}

          {/* Main Content Area with Sphere Background */}
          <main className="flex-1 relative overflow-y-auto bg-gray-950">
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
              <ThreeSphere zoomMultiplier={5.5} />
            </div>
            <div className="relative z-10 p-4 md:p-6">
              <Routes>
              <Route path="/" element={<DashboardHome />} />
              <Route path="ironworkers" element={<IronworkersPage />} />
              <Route path="project-managers" element={<ProjectManagersPage />} />
              <Route path="foremen" element={<ForemenPage />} />
              <Route path="contacts" element={<ContactsCRMPage />} />
              <Route path="timesheets" element={<ComingSoonPage />} />
              <Route path="cost-codes" element={<CostCodesCRM />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/:id" element={<ProjectDetailPage />} />
              <Route path="seats" element={<SeatsPage />} />
              <Route path="bids/*" element={<ComingSoonPage />} />
              <Route path="company-insights" element={<ComingSoonPage />} />
              <Route path="project-templates" element={<ComingSoonPage />} />
              <Route path="equipment-requests" element={<ComingSoonPage />} />
              <Route path="punch-lists" element={<ComingSoonPage />} />
              <Route path="crm-leads" element={<ComingSoonPage />} />
              <Route path="organization-settings" element={<ComingSoonPage />} />
              <Route path="billing-seats" element={<ComingSoonPage />} />
              <Route path="*" element={<Navigate to="/myteconstruction/dashboard/ironworkers" />} />
            </Routes>
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

function DashboardHome() {
  const { auth } = useContext(AuthContext);
  let userName = "User";
  if (auth.token) {
    try {
      const decoded = jwtDecode(auth.token);
      if (decoded.name) userName = decoded.name;
    } catch (e) {
      console.error("Failed to decode token", e);
    }
  }

  return (
    <section className="space-y-8">
      {/* Hero */}
      <header className="space-y-2">
        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight leading-snug">
          🚀 Welcome to your <span className="text-blue-400">Digital Playground</span>, {userName}!
        </h1>
        <p className="text-md md:text-lg text-gray-400">
          We are adding features everyday as we build out the foundations of custom constructiontion-tech for the Steel Industry.
        </p>
      </header>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpiData.map(({ title, value, delta, deltaPositive, icon: Icon }) => (
          <div
            key={title}
            className="bg-gray-900 p-5 rounded-lg shadow flex flex-col justify-between border border-gray-800"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-300">{title}</h3>
              <Icon className="h-4 w-4 text-blue-400" />
            </div>
            <div className="mt-3 text-2xl font-bold text-white">{value}</div>
            <p
              className={`text-xs mt-1 flex items-center ${
                deltaPositive ? "text-green-400" : "text-red-400"
              }`}
            >
              {deltaPositive ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
              {delta}
            </p>
          </div>
        ))}
      </div>

      {/* Project overview */}
      <div className="bg-gray-900 p-6 rounded-lg shadow border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Project Overview</h2>
          <button className="text-sm text-blue-500 hover:text-blue-400">View All</button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {projectOverview.map(({ name, progress }) => (
            <div key={name} className="bg-gray-800 p-4 rounded-lg">
              <h3 className="font-medium text-gray-300 mb-2">{name}</h3>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-400">Progress</span>
                <span className="text-blue-400">{progress}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${progress}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deadlines */}
      <div className="bg-gray-900 p-6 rounded-lg shadow border border-gray-800">
        <h2 className="text-xl font-semibold text-white mb-4">Upcoming Deadlines</h2>
        <ul className="space-y-2">
          {upcomingDeadlines.map(({ id, projectName, daysLeft }) => (
            <li key={id} className="flex justify-between p-3 bg-gray-800 rounded-md">
              <span>{projectName}</span>
              <span
                className={
                  daysLeft <= 3 ? "text-red-400" : daysLeft <= 7 ? "text-yellow-400" : "text-green-400"
                }
              >
                {daysLeft} days left
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}


function PlaceholderPage({ name }) {
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold text-white">{name}</h1>
      <p className="text-gray-400">Manage your {name.toLowerCase()} for steel construction projects.</p>
      <div className="bg-gray-900 p-6 rounded-lg shadow-lg border border-gray-800 mt-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Building2 className="w-16 h-16 text-blue-500 mx-auto mb-4 opacity-50" />
            <h2 className="text-xl font-medium text-gray-300 mb-2">No {name} Data Yet</h2>
            <p className="text-gray-500 max-w-md mb-4">
              Start adding {name.toLowerCase()} to your organization to see them listed here.
            </p>
            <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 flex items-center gap-2 mx-auto">
              <Plus className="w-4 h-4" />
              <span>Add {name.replace(/s$/, "")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
PlaceholderPage.propTypes = {
  name: PropTypes.string.isRequired,
}

export default OrganizationDashboard
